package com.godaddy.android.colorpicker

import androidx.compose.foundation.*
import androidx.compose.foundation.gestures.*
import androidx.compose.foundation.layout.*
import androidx.compose.runtime.*
import androidx.compose.ui.*
import androidx.compose.ui.geometry.*
import androidx.compose.ui.graphics.*
import androidx.compose.ui.graphics.drawscope.*
import androidx.compose.ui.input.pointer.*
import androidx.compose.ui.unit.*
import kotlin.math.*

/**
 * Alpha side bar Component that invokes onAlphaChanged when the value is mutated.
 *
 * @param modifier modifiers to set on the Alpha Bar
 * @param currentColor the initial color to set on the alpha bar.
 * @param onAlphaChanged the callback that is invoked when alpha value changes. 0 - 1.
 */
@Composable
internal fun AlphaBar(
    modifier: Modifier = Modifier,
    currentColor: HsvColor,
    onAlphaChanged: (Float) -> Unit
) {
    val currentColorToAlphaBrush = remember(currentColor) {
        Brush.horizontalGradient(
            listOf(
                currentColor.copy(alpha = 1.0f).toColor(),
                Color(0x00ffffff)
            )
        )
    }
    Canvas(
        modifier = modifier
            .fillMaxSize()
            .pointerInput(Unit) {
                awaitPointerEventScope {
                    while (true) {
                        val down = awaitFirstDown()
                        onAlphaChanged(
                            getAlphaFromPosition(
                                down.position.x,
                                this.size.width.toFloat()
                            ).coerceIn(0f, 1f)
                        )
                        drag(down.id) { change ->
                            change.consume()
                            onAlphaChanged(
                                getAlphaFromPosition(
                                    change.position.x,
                                    this.size.width.toFloat()
                                ).coerceIn(0f, 1f)
                            )
                        }
                    }
                }
            }
    ) {
        clipRect {
            drawCheckeredBackground()
        }

        drawAlphaBar(currentColorToAlphaBrush)

        val position = getPositionFromAlpha(
            color = currentColor,
            maxWidth = this.size.width
        )
        drawHorizontalSelector(amount = position)
    }
}

private fun DrawScope.drawAlphaBar(alphaBrush: Brush) {
    drawRect(alphaBrush)
    drawRect(Color.Gray, style = Stroke(0.5.dp.toPx()))
}

private fun DrawScope.drawCheckeredBackground() {
    val darkColor = Color.LightGray
    val lightColor = Color.White

    val gridSizePx = 8.dp.toPx()
    val cellCountX = ceil(this.size.width / gridSizePx).toInt()
    val cellCountY = ceil(this.size.height / gridSizePx).toInt()
    for (i in 0 until cellCountX) {
        for (j in 0 until cellCountY) {
            val color = if ((i + j) % 2 == 0) darkColor else lightColor

            val x = i * gridSizePx
            val y = j * gridSizePx
            drawRect(color, Offset(x, y), Size(gridSizePx, gridSizePx))
        }
    }
}

private fun getPositionFromAlpha(color: HsvColor, maxWidth: Float): Float {
    val alpha = 1 - color.alpha
    return maxWidth * alpha
}

/**
 * @return new alpha calculated from the maxWidth
 */
private fun getAlphaFromPosition(x: Float, maxWidth: Float): Float {
    return 1 - x / maxWidth
}
